/* The rule forms the Rules screen and its drawer open: authoring a rule and publishing a version.
 * Binding a rule is `BindRuleForm`. Moved out of `RulesScreen.tsx` unchanged in behaviour, so the screen
 * file holds the screen and these can be opened from either the list or the drawer. */

import { useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { suggestionIdFromTags, useRuleDraft } from '@/features/rulegen';
import type { RuleDraft as SuggestedRuleDraft } from '@/features/rulegen';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { SelectField, TextField } from '@/shared/ui/Field';

import { useCreateRule, usePublishVersion, useRuleVersions } from '../api/rules';
import type { Rule, RuleKind } from '../api/rules';
import type { QualityDimension } from '@/shared/api/generated/rules';

import { dimensionFromTags, dimensionTag } from '../model/dimensions';
import { AUTHORABLE_KINDS, KIND_SHAPES, invalidParameters } from '../model/parameters';

import { DimensionChoice, DimensionLine } from './RuleDimension';
import { RuleParameterFields } from './RuleParameterFields';

/* Authoring a rule: name, kind, owner. Nothing about what it checks.
 *
 * **Used to also collect the first version's columns and parameters and publish it in the same
 * submit.** `POST /v1/rules` never took them — `RuleService.create` is name/kind/owner/description/
 * tags, full stop, and `RuleExpression` has always lived on `RuleVersion`, a separate aggregate
 * published through its own call. Bundling the two here meant target columns were asked for once
 * on this form and asked for *again* on `PublishVersionForm` the moment a second version was
 * needed — the same field in two places, one of which a reader would not meet until later.
 *
 * **Now this form does the one call `RuleService.create` actually wants**, and publishing stays
 * where the version history already is: `RuleDetail` renders `PublishVersionForm` for any rule
 * still in `draft`, so the very next thing a reader sees after creating one is where to publish its
 * first version — with the columns field asked exactly once, on the form built for it.
 *
 * A rule with no published version is unexecutable (`RuleExpression` lives on the version, and a
 * score is attributed to a version so "a score computed months ago can still be explained" stays
 * true) — that has not changed. What changed is that this form no longer pretends authoring and
 * publishing are one action; `RuleDetail`'s own note said as much before this form caught up to it:
 * "A version freezes an expression, so the form belongs beside the history of what has already been
 * frozen."
 */
/* **Renders the fields and nothing around them — no card, no heading.** Both of this screen's forms
 * used to own a `Card` and sit *inside* the pane, which put a five-field form between the toolbar and
 * the table a reader was looking at. They open in a `Modal` now, which owns the title and hint, so
 * the same copy keys serve both. `AddUserForm` made the same move for the same reason. */
export function CreateRuleForm({
  onCreated,
  onCancel,
  draft,
  steps,
}: {
  /** What happens after saving, drawn above the buttons in place of the sentence that says it. The
   * drawer passes the design's three steps; a caller with no room passes nothing. */
  readonly steps?: ReactNode;
  readonly onCreated: (rule: Rule) => void;
  /** Dismiss without submitting. Absent renders no Cancel — see `AddUserForm`'s note. */
  readonly onCancel?: (() => void) | undefined;
  /** A suggestion's field values, when the reader arrived from `/suggestions`.
   *
   * **Prefilled, never submitted for them.** §2's whole point is that a recommendation becomes a
   * rule by a person reviewing and saving it, so this fills the fields and presses nothing — and
   * the banner above says the values are proposed, because a form that opens already filled is
   * otherwise indistinguishable from one somebody has already checked.
   */
  readonly draft?: SuggestedRuleDraft | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState(draft?.name ?? '');
  const [owner, setOwner] = useState('');
  const [kind, setKind] = useState<RuleKind>(draft?.kind ?? 'not_null');
  /* A custom SQL rule's dimension, if the author chooses it now. It travels in the rule's tags and
     the publish form reads it back — see `dimensionTag`. */
  const [sqlDimension, setSqlDimension] = useState<QualityDimension | ''>('');

  const create = useCreateRule();
  const shape = KIND_SHAPES[kind];

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate(
            /* **The provenance tag and the rationale travel with the create**, and they are the
               reason the second hop works. `POST /v1/rules` already accepts `description` and
               `tags`, so `suggested-from:<id>` needs no schema change to `core.rules` and no
               knowledge of suggestions in that module — and the created rule then carries a durable
               reference the publish form reads back to seed the version's columns and parameters,
               which router state could not survive a reload to do.

               The tag comes from the server on `RuleDraft.tags` rather than being composed here:
               one authority for what the tag says, so the write and the read cannot disagree. */
            withDimensionTag(
              draft === undefined
                ? { name, kind, owner }
                : {
                    name,
                    kind,
                    owner,
                    description: draft.description,
                    tags: [...draft.tags],
                  },
              kind === 'custom_sql' && sqlDimension !== '' ? sqlDimension : null,
            ),
            { onSuccess: onCreated },
          );
        }}
      >
        {/* **Says the fields are proposed, and it is required rather than decorative.** §11: a
            prefilled form must read as a recommendation to review, not as something already
            checked. `requires_review` is on the wire for the same reason — a screen that decided
            this for itself is a screen that can quietly stop doing it. */}
        {draft !== undefined && draft.requires_review && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Alert tone="notice" title={t('rules.create.fromSuggestion')}>
              {draft.rationale}
            </Alert>
          </div>
        )}
        {create.isError && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Alert tone="error">{create.error.message}</Alert>
          </div>
        )}

        <div className="lq-grid">
          <TextField
            label={t('rules.field.name')}
            value={name}
            onChange={setName}
            required
            maxLength={200}
          />
          {/* Kind and its dimension share one cell, so the dimension reads as a fact of the kind
              directly under it, as the design draws it. */}
          <div className="lq-ru__kindField">
            <SelectField
              label={t('rules.field.kind')}
              value={kind}
              onChange={setKind}
              options={AUTHORABLE_KINDS.map((option) => ({
                value: option,
                label: t(`rules.kind.${option}`),
              }))}
              hint={t(shape.measuresKey)}
            />
            {kind === 'custom_sql' ? (
              <DimensionChoice
                value={sqlDimension}
                onChange={setSqlDimension}
                emptyLabel={t('rules.dimension.chooseLater')}
              />
            ) : (
              <DimensionLine kind={kind} />
            )}
          </div>
          <TextField
            label={t('rules.field.owner')}
            value={owner}
            onChange={setOwner}
            required
            maxLength={200}
            hint={t('rules.field.ownerHint')}
          />
        </div>

        {/* What the columns field used to be here for. Said once, rather than asked twice: this
            form used to collect a version's columns and parameters and publish version 1.0.0 in
            the same submit — the same fields `PublishVersionForm` already asks for on the rule's
            own page, the moment a second version is needed. One field in one place now. */}
        {steps ?? (
          <p className="lq-muted" style={{ marginTop: 'var(--space-3)' }}>
            {t('rules.create.publishSeparately')}
          </p>
        )}

        {/* Cancel first in the DOM and on screen: the safe answer is the first tab stop and the one
            that writes goes last. `.lq-form-actions`, not the confirm dialog's row — see
            `AddUserForm` for why those two stay separate. */}
        <div className="lq-form-actions">
          {onCancel === undefined ? null : (
            <Button type="button" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
          <Button tone="primary" type="submit" busy={create.isPending}>
            {t('rules.create.submit')}
          </Button>
        </div>
      </form>
    </>
  );
}

/** A create request with a custom SQL rule's chosen dimension added to its tags. */
function withDimensionTag<T extends { readonly tags?: readonly string[] }>(
  request: T,
  dimension: QualityDimension | null,
): T {
  if (dimension === null) return request;
  return { ...request, tags: [...(request.tags ?? []), dimensionTag(dimension)] };
}

function nextPatchVersion(ver: string): string {
  const parts = ver.split('.').map((p) => parseInt(p, 10));
  const major = parts[0];
  const minor = parts[1];
  const patch = parts[2];
  if (
    parts.length === 3 &&
    major !== undefined &&
    minor !== undefined &&
    patch !== undefined &&
    !isNaN(major) &&
    !isNaN(minor) &&
    !isNaN(patch)
  ) {
    return `${String(major)}.${String(minor)}.${String(patch + 1)}`;
  }
  return `${ver}.1`;
}

export function PublishVersionForm({
  rule,
  onPublished,
  bare = false,
}: {
  readonly rule: Rule;
  readonly onPublished: () => void;
  /** Without its card, for a dialog that already carries the title. The hint stays, as the dialog's
   * first line: on an active rule it is what says publishing changes nothing already running. */
  readonly bare?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const versions = useRuleVersions(rule.id);
  const items = versions.data?.items ?? [];
  const latestVersion = items[0];
  const suggestedVersion = latestVersion
    ? nextPatchVersion(latestVersion.version)
    : '1.0.0';

  const [version, setVersion] = useState(suggestedVersion);
  const [columnsStr, setColumnsStr] = useState('');
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [notes, setNotes] = useState('Published version update');
  /* Custom SQL only: the dimension the version declares, seeded from the one chosen at creation. */
  const [sqlDimension, setSqlDimension] = useState<QualityDimension | ''>(
    () => dimensionFromTags(rule.tags) ?? latestVersion?.dimension ?? '',
  );
  const needsDimension = rule.kind === 'custom_sql';

  /* **Seeded from the latest version once the versions arrive**, which is what makes this a
   * *revision* rather than a re-entry: publishing 1.0.1 of a pattern rule almost always means
   * changing the pattern, and retyping the other parameters is how one gets accidentally dropped.
   *
   * In an effect rather than `useState`'s initialiser, and that is a fix rather than a preference —
   * the initialiser runs on the first render, when `useRuleVersions` is still in flight, so it
   * captured an empty object and the field rendered blank. Found by reading the rendered input's
   * value rather than the code, which is the only place it shows.
   *
   * **Guarded on `parameters` being empty**, so it seeds once and never overwrites typing. An author
   * who clears a field leaves `{ pattern: '' }` behind — non-empty — so even a deliberately emptied
   * field is not re-filled behind them. */
  useEffect(() => {
    const carried = latestVersion?.expression.parameters;
    if (carried === undefined) return;
    setParameters((current) =>
      Object.keys(current).length === 0 ? { ...carried } : current,
    );
  }, [latestVersion]);

  /* **The second half of §2's loop, and the provenance tag is what makes it possible.**
   *
   * Creating a rule from a suggestion is two calls — `POST /v1/rules` takes the name, kind and
   * owner, and the columns and parameters live on the *version* — so the suggestion's values have
   * to survive the hop. Router state does not: a reload, a bookmark or a link shared with the
   * second pair of eyes all arrive here with nothing, and the author retypes exactly the values the
   * recommendation existed to supply.
   *
   * So the created rule carries `suggested-from:<id>` in its own `tags`, and this reads it back.
   * `core.rules` needed no schema change and knows nothing about suggestions; the tag is a
   * reference, and reading it here is what turns it from a label into a mechanism.
   *
   * **Only for a rule with no version yet.** `latestVersion` is the stronger source once one
   * exists — publishing 1.0.1 is a revision of what was frozen, not a re-entry of what was
   * originally recommended — and seeding from the suggestion then would silently revert an author's
   * earlier edit under a version label that reads like a patch.
   *
   * Guarded on empty for the effect above's own reason: it fills once and never overwrites typing,
   * so a field an author deliberately cleared is not re-filled behind them. */
  const suggestionId = suggestionIdFromTags(rule.tags);
  const seed = useRuleDraft(
    versions.isSuccess && items.length === 0 && suggestionId !== null
      ? suggestionId
      : undefined,
  );
  useEffect(() => {
    if (!seed.isSuccess) return;
    const proposed = seed.data;
    setColumnsStr((current) => (current === '' ? proposed.columns.join(', ') : current));
    setParameters((current) =>
      Object.keys(current).length === 0 ? { ...proposed.parameters } : current,
    );
  }, [seed.isSuccess, seed.data]);

  /* **The columns are carried forward too, and their absence was a silent defect.** The effect
   * above seeded `parameters` on the argument that "retyping the other parameters is how one gets
   * accidentally dropped" — and `columns` is subject to exactly that and was not seeded, so
   * publishing 1.0.1 of a rule carried its pattern and silently lost the column the pattern was
   * written against.
   *
   * It did not fail loudly either, which is what made it worth fixing rather than noting.
   * `COLUMNS_OPTIONAL_AT_PUBLISH` lets `not_null` and `unique` publish with none, so for those two
   * the revision was accepted and the new version measured whatever `ExecutionService.plan`
   * resolves from the dataset's schema instead of the named column — a different measurement under
   * a version label that reads like a patch. For the other kinds the server refused it, which is
   * the better half of the same bug.
   *
   * Guarded on empty for the seeding effect's own reason: it fills once and never overwrites
   * typing, so an author who deliberately clears the field is not re-filled behind them. */
  useEffect(() => {
    const carried = latestVersion?.expression.columns;
    if (carried === undefined) return;
    setColumnsStr((current) => (current === '' ? carried.join(', ') : current));
  }, [latestVersion]);

  useEffect(() => {
    if (suggestedVersion) {
      setVersion(suggestedVersion);
    }
  }, [suggestedVersion]);

  const publish = usePublishVersion();

  const handlePublish = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    /* Refused here rather than sent. The server's message is clear — "pattern requires parameter(s)
       pattern; a rule that cannot be executed must not be publishable" — and an author reads it far
       better beside the field than as a banner over a form whose field they then have to find. */
    if (invalidParameters(rule.kind, parameters).size > 0) return;
    /* The server refuses a custom SQL version with no dimension; say so beside the field instead. */
    if (needsDimension && sqlDimension === '') return;

    const columns = columnsStr
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    publish.mutate(
      {
        ruleId: rule.id,
        version: version.trim() || suggestedVersion,
        expression: {
          kind: rule.kind,
          columns,
          parameters,
        },
        dimension: needsDimension && sqlDimension !== '' ? sqlDimension : undefined,
        notes,
      },
      {
        onSuccess: onPublished,
      },
    );
  };

  const hint =
    /* **Three hints, and the third is load-bearing.** This form is now reachable for an active
           rule, where the question a steward has is not "what is the latest version" but "am I
           about to change production". They are not: a binding pins an exact `rule_version_id`
           rather than following latest, so a new version is inert until somebody rebinds. Saying so
           here rather than only in a code comment, because the person who needs it is reading the
           screen. */
    latestVersion && rule.state === 'active'
      ? t('rules.version.publish.liveRule', {
          version: latestVersion.version,
          kind: t(`rules.kind.${rule.kind}`),
        })
      : latestVersion
        ? t('rules.version.publish.latest', {
            version: latestVersion.version,
            kind: t(`rules.kind.${rule.kind}`),
          })
        : t('rules.version.publish.kindOnly', { kind: t(`rules.kind.${rule.kind}`) });

  const form = (
    <form onSubmit={handlePublish}>
      {publish.isError && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <Alert tone="error">{publish.error.message}</Alert>
        </div>
      )}
      <div className="lq-grid">
        <TextField
          label={t('rules.version.publish.versionLabel')}
          value={version}
          onChange={setVersion}
          required
          hint={
            latestVersion
              ? t('rules.version.publish.versionHint', {
                  latest: latestVersion.version,
                })
              : t('rules.version.publish.versionFirstHint')
          }
        />
        {/* A table-level kind (`row_count_anomaly`, `custom_sql`) reads no column, so the field is
            optional and blank means the whole table — said here, because a bare "Target Columns"
            field beside a required-looking example invites typing a column the check never uses. */}
        <TextField
          label={t('rules.version.publish.columnsLabel')}
          value={columnsStr}
          onChange={setColumnsStr}
          placeholder={
            KIND_SHAPES[rule.kind].needsColumns
              ? t('rules.field.columnsPlaceholder')
              : t('rules.version.publish.columnsTableLevelPlaceholder')
          }
          hint={
            KIND_SHAPES[rule.kind].needsColumns
              ? undefined
              : t('rules.version.publish.columnsTableLevel')
          }
        />
        <TextField
          label={t('rules.version.publish.notesLabel')}
          value={notes}
          onChange={setNotes}
        />
      </div>
      {needsDimension ? (
        <DimensionChoice
          value={sqlDimension}
          onChange={setSqlDimension}
          emptyLabel={t('rules.dimension.choose')}
          error={
            submitted && sqlDimension === '' ? t('rules.dimension.required') : undefined
          }
        />
      ) : (
        <DimensionLine kind={rule.kind} />
      )}

      {/* **The kind's own parameters, which this form could not ask for at all.** It sent
            `parameters: {}` unconditionally, so `pattern`, `range`, `accepted_values`, `freshness`,
            `referential_integrity` and `custom_sql` were refused by the server with a message no
            field on screen could answer — a shipped dead end for six of the nine kinds. The fields
            come from `KIND_SHAPES`, the same table the author form reads, so the two cannot ask for
            different things. */}
      <RuleParameterFields
        kind={rule.kind}
        values={parameters}
        onChange={(parameter, value) => {
          setParameters((current) => ({ ...current, [parameter]: value }));
        }}
        submitted={submitted}
      />
      <div style={{ marginTop: 'var(--space-4)' }}>
        <Button tone="primary" type="submit" busy={publish.isPending}>
          {t('rules.version.publish.submit')}
        </Button>
      </div>
    </form>
  );

  if (bare) {
    return (
      <>
        <p className="lq-ru__formHint">{hint}</p>
        {form}
      </>
    );
  }

  return (
    <Card title={`${t('rules.version.publish.title')} — ${rule.name}`} hint={hint}>
      {form}
    </Card>
  );
}
